import { BRAZIL_STATE_SHAPES } from '@/data/brazilGeo';
import { CITY_POINTS } from '@/data/cityPoints';
import { normalize, isRecent } from '@/lib/format';

/**
 * Camada de geolocalização: converte (UF, cidade) em coordenadas do mapa.
 * Pontos só aparecem em cidades reais cadastradas em CITY_POINTS; quando a
 * cidade não está no índice, usamos o centro do estado e marcamos como aproximado.
 */
const key = (uf, city) => `${uf}|${normalize(city)}`;
const CITY_INDEX = Object.fromEntries(CITY_POINTS.map((p) => [key(p.uf, p.name), p]));
export const STATE_CENTROID = Object.fromEntries(BRAZIL_STATE_SHAPES.map((s) => [s.uf, { x: s.cx, y: s.cy }]));
export const STATE_CAPITAL = CITY_POINTS.reduce((acc, p) => (acc[p.uf] ? acc : { ...acc, [p.uf]: p }), {});
export const citiesOfState = (uf) => CITY_POINTS.filter((p) => p.uf === uf).map((p) => p.name);

export function locate(uf, city) {
  const p = CITY_INDEX[key(uf, city)];
  if (p) return { x: p.x, y: p.y, approx: false };
  const c = STATE_CENTROID[uf] || STATE_CAPITAL[uf];
  return c ? { x: c.x, y: c.y, approx: true } : null;
}

/** Agrega empresas e oportunidades recentes por ponto do mapa. */
export function buildMapLayers(companies, opportunities) {
  const points = {};
  const add = (uf, city) => {
    const k = key(uf, city);
    if (!points[k]) {
      const loc = locate(uf, city);
      if (!loc) return null;
      points[k] = { id: k, uf, city, ...loc, companies: [], recentOpps: [] };
    }
    return points[k];
  };
  companies.forEach((c) => add(c.location.uf, c.location.city)?.companies.push(c));
  opportunities.filter((o) => isRecent(o.publishedAt)).forEach((o) => add(o.location.uf, o.location.city)?.recentOpps.push(o));

  const stateStats = {};
  companies.forEach((c) => {
    stateStats[c.location.uf] = stateStats[c.location.uf] || { companies: 0, opportunities: 0 };
    stateStats[c.location.uf].companies += 1;
  });
  opportunities.forEach((o) => {
    stateStats[o.location.uf] = stateStats[o.location.uf] || { companies: 0, opportunities: 0 };
    stateStats[o.location.uf].opportunities += 1;
  });

  const connections = [];
  opportunities
    .filter((o) => Date.now() - new Date(o.publishedAt).getTime() < 21 * 86400000)
    .forEach((o) => {
      const from = locate(o.location.uf, o.location.city);
      (o.targetUfs || []).forEach((t) => {
        const to = STATE_CAPITAL[t] || STATE_CENTROID[t];
        if (from && to && t !== o.location.uf) connections.push({ id: `${o.id}-${t}`, from, to, fromUf: o.location.uf, toUf: t, hot: isRecent(o.publishedAt) });
      });
    });
  return { points: Object.values(points), stateStats, connections };
}

export const curvePath = (a, b) => {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  const d = Math.hypot(b.x - a.x, b.y - a.y);
  return `M${a.x},${a.y} Q${mx},${my - d * 0.28} ${b.x},${b.y}`;
};
